import { Component, EventEmitter, Input, OnInit, Output, forwardRef } from "@angular/core";
import { ControlValueAccessor, FormBuilder, FormControl, FormGroup, NG_VALUE_ACCESSOR } from "@angular/forms";
import { takeUntil } from "rxjs";
import dayjs from "@gtmhub/dayjs";
import { MetricDeadLine, MetricDeadLineEnum, metricFormStrings } from "@gtmhub/okrs/metrics/utils";
import { DestroyService } from "@webapp/core/util/services/destroy.service";
import { Deadline } from "@webapp/okrs/metrics/models/metric.models";

interface SelectFormFields {
  deadline: FormControl<MetricDeadLineEnum>;
}

@Component({
  selector: "metric-deadline-selector",
  templateUrl: "./metric-deadline-selector.component.html",
  styleUrls: ["./metric-deadline-selector.component.less"],
  providers: [
    DestroyService,
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MetricDeadlineSelectorComponent),
      multi: true,
    },
  ],
})
export class MetricDeadlineSelectorComponent implements OnInit, ControlValueAccessor {
  @Input() public selectedOption: MetricDeadLine = MetricDeadLineEnum.NONE;
  @Input() public softDeadline: Date;
  @Input() public hardDeadline: Date;

  @Input() public uiId: string;
  @Input() public a11yDescription: string;
  @Input() public minHardDeadline: Date;
  @Input() public maxHardDeadline: Date;
  @Input() public minSoftDeadline: Date;
  @Input() public maxSoftDeadline: Date;
  @Input() public milestoneDeadline: Date;
  @Input() public showLabels = true;
  @Input() public borderless = false;
  @Input() public showValidationHint = false;

  @Output() public readonly softDeadlineChange = new EventEmitter<Date>();
  @Output() public readonly hardDeadlineChange = new EventEmitter<Date>();
  @Output() public readonly selectedChange = new EventEmitter<string>();

  public selectForm: FormGroup<SelectFormFields>;
  public options: MetricDeadLine[] = [...Object.values(MetricDeadLineEnum)];

  constructor(private formBuilder: FormBuilder, private destroyService$: DestroyService) {}

  public ngOnInit(): void {
    this.selectForm = this.formBuilder.group({
      deadline: this.formBuilder.control(this.selectedOption),
    });

    this.subscribeToValueChange();
  }

  public writeValue(value: Deadline): void {
    this.selectedOption = value.useDeadline;
    this.hardDeadline = new Date(value.dueDate);
    this.softDeadline = new Date(value.softDueDate);

    this.selectForm.controls.deadline.setValue(value.useDeadline, { emitEvent: false });
  }

  public propagateChange;
  public registerOnChange(fn): void {
    this.propagateChange = fn;
  }

  public registerOnTouched(): void {
    // required by the ControlValueAccessor interface
  }

  private emitPropagateChange(): void {
    if (typeof this.propagateChange !== "function") return;

    this.propagateChange({
      useDeadline: this.selectedOption,
      dueDate: this.selectedOption === MetricDeadLineEnum.NONE ? metricFormStrings.default.date : this.hardDeadline.toISOString(),
      softDueDate: this.selectedOption === MetricDeadLineEnum.HARD_AND_SOFT_DEADLINE ? this.softDeadline.toISOString() : metricFormStrings.default.date,
    });
  }

  public emitHardDeadlineChange(change: Date): void {
    this.hardDeadlineChange.emit(change);
    this.emitPropagateChange();
  }

  public emitSoftDeadlineChange(change: Date): void {
    this.softDeadlineChange.emit(change);
    this.emitPropagateChange();
  }

  public checkIfSoftDateIsDisabled = (date: Date): boolean => {
    return this.checkIfDateIsDisabled(date, this.minSoftDeadline, this.maxSoftDeadline);
  };

  public checkIfHardDateIsDisabled = (date: Date): boolean => {
    return this.checkIfDateIsDisabled(date, this.minHardDeadline, this.maxHardDeadline);
  };

  private checkIfDateIsDisabled(date: Date, minDate: Date, maxDate: Date): boolean {
    const dueDate = dayjs(date).startOf("day");

    const minDateCheck = minDate ? dueDate.isBefore(dayjs(minDate).startOf("day")) : false;
    const maxDateCheck = maxDate ? dueDate.isAfter(dayjs(maxDate).endOf("day")) : false;
    const milestoneDateCheck = this.milestoneDeadline ? dueDate.isBefore(dayjs(this.milestoneDeadline).endOf("day")) : false;

    return minDateCheck || maxDateCheck || milestoneDateCheck;
  }

  private subscribeToValueChange(): void {
    this.selectForm.controls.deadline.valueChanges.pipe(takeUntil(this.destroyService$)).subscribe((value) => {
      this.selectedOption = value;

      this.selectedChange.emit(value);
      this.emitPropagateChange();
    });
  }
}
